import { useState } from 'react'
import {
  ChevronDown, ChevronRight, Users, Home, BookOpen,
  Briefcase, Phone, Scale, Stethoscope, MapPin, MessageCircle,
  Eye, EyeOff, Calendar
} from 'lucide-react'
import { Badge } from '../ui'

/**
 * ColloquioCard — Card espandibile per un colloquio nella lista
 *
 * Props:
 *   colloquio   - Oggetto colloquio
 *   isCoord     - Boolean
 */

const TIPO_CONFIG = {
  colloquio_famiglia: { label: 'Famiglia', icon: Home, color: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20' },
  colloquio_scuola: { label: 'Scuola', icon: BookOpen, color: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20' },
  equipe_servizi_sociali: { label: 'Servizi sociali', icon: Briefcase, color: 'text-purple-600 bg-purple-50 dark:bg-purple-900/20' },
  equipe_interna: { label: 'Equipe interna', icon: Users, color: 'text-green-600 bg-green-50 dark:bg-green-900/20' },
  incontro_specialista: { label: 'Specialista', icon: Stethoscope, color: 'text-teal-600 bg-teal-50 dark:bg-teal-900/20' },
  udienza_tribunale: { label: 'Tribunale', icon: Scale, color: 'text-red-600 bg-red-50 dark:bg-red-900/20' },
  visita_domiciliare: { label: 'Visita domiciliare', icon: MapPin, color: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20' },
  telefonata: { label: 'Telefonata', icon: Phone, color: 'text-gray-600 bg-gray-50 dark:bg-gray-900/20' },
  altro: { label: 'Altro', icon: MessageCircle, color: 'text-gray-600 bg-gray-50 dark:bg-gray-900/20' }
}

export default function ColloquioCard({ colloquio: coll, isCoord }) {
  const [expanded, setExpanded] = useState(false)

  const config = TIPO_CONFIG[coll.tipo] || TIPO_CONFIG.altro
  const Icon = config.icon
  const isSoloCoord = coll.visibilita === 'solo_coordinatore'

  // Difesa in profondità: non renderizzare colloqui riservati per educatore
  if (!isCoord && isSoloCoord) return null

  const hasContent = coll.contenuto || coll.decisioni || (coll.partecipanti && coll.partecipanti.length > 0)

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Header — sempre visibile */}
      <button
        onClick={() => hasContent && setExpanded(!expanded)}
        className={`w-full flex items-center gap-3 p-3 text-left ${hasContent ? 'hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer' : 'cursor-default'} transition-colors`}
      >
        {/* Icona tipo */}
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${config.color}`}>
          <Icon className="h-4.5 w-4.5" />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
              {coll.titolo}
            </p>
            {isSoloCoord && (
              <EyeOff className="h-3 w-3 text-gray-400 flex-shrink-0" title="Solo coordinatore" />
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
            <span>{config.label}</span>
            <span>&mdash;</span>
            <span>
              {new Date(coll.dataEvento).toLocaleDateString('it-IT', {
                day: 'numeric', month: 'short', year: 'numeric'
              })}
            </span>
            {coll.partecipanti && coll.partecipanti.length > 0 && (
              <>
                <span>&mdash;</span>
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {coll.partecipanti.length}
                </span>
              </>
            )}
            {coll.isDatoStorico && (
              <span className="text-[10px] text-gray-400 bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">
                storico
              </span>
            )}
          </div>
        </div>

        {/* Toggle */}
        {hasContent && (
          <div className="flex-shrink-0">
            {expanded
              ? <ChevronDown className="h-4 w-4 text-gray-400" />
              : <ChevronRight className="h-4 w-4 text-gray-400" />
            }
          </div>
        )}
      </button>

      {/* Contenuto espanso */}
      {expanded && (
        <div className="px-3 pb-3 pt-0 border-t border-gray-100 dark:border-gray-700">
          <div className="pl-12 space-y-3 pt-3">
            {/* Contenuto */}
            {coll.contenuto && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Contenuto</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
                  {coll.contenuto}
                </p>
              </div>
            )}

            {/* Decisioni */}
            {coll.decisioni && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Decisioni / Azioni</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
                  {coll.decisioni}
                </p>
              </div>
            )}

            {/* Partecipanti */}
            {coll.partecipanti && coll.partecipanti.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Partecipanti</p>
                <div className="flex flex-wrap gap-1.5">
                  {coll.partecipanti.map((p, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-md"
                    >
                      {p.nome}
                      {p.ruolo && (
                        <span className="text-gray-400">({p.ruolo})</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Prossimo incontro */}
            {coll.prossimoIncontro && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Calendar className="h-3.5 w-3.5" />
                <span>
                  Prossimo incontro:{' '}
                  <strong>
                    {new Date(coll.prossimoIncontro).toLocaleDateString('it-IT', {
                      day: 'numeric', month: 'long', year: 'numeric'
                    })}
                  </strong>
                </span>
              </div>
            )}

            {/* Registrato da */}
            {coll.registratoDaNome && (
              <p className="text-[10px] text-gray-400">
                Registrato da {coll.registratoDaNome}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
